import React, { createContext, useContext, useCallback, useRef } from 'react';
import {
  getBulkPublishStatus,
  getContentPublishStatus,
  ContentPublishStatus,
} from '../api/ayrshareApi';
import { useAuth } from '../AuthContext';

// Batches per-card publish-status lookups into one bulk request. The TEND
// view mounts ~45 SmartPublishButtons in a single render pass; each firing
// its own round-trip saturated the browser connection pool (the reason
// eager status was turned off in 1ad6092). Buttons that mount within the
// same BATCH_WINDOW_MS share one request per brand.

interface StatusCacheEntry {
  status: ContentPublishStatus;
  timestamp: number;
}

interface PendingWaiter {
  contentId: number;
  resolve: (status: ContentPublishStatus | null) => void;
}

interface PublishStatusContextType {
  /** Batched + cached lookup — safe to call from every card on mount. */
  getPublishStatus: (brandId: number, contentId: number) => Promise<ContentPublishStatus | null>;
  /** Fresh single fetch, bypasses cache and updates it — for expand/publish/delete. */
  refreshPublishStatus: (brandId: number, contentId: number) => Promise<ContentPublishStatus | null>;
}

const PublishStatusContext = createContext<PublishStatusContextType | null>(null);

const CACHE_TTL = 60000; // publish state changes rarely outside explicit refreshes
const BATCH_WINDOW_MS = 50; // one render pass of mounting cards

export const PublishStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const cacheRef = useRef<Map<string, StatusCacheEntry>>(new Map());
  const batchRef = useRef<Map<number, PendingWaiter[]>>(new Map());
  const batchTimerRef = useRef<Map<number, number>>(new Map());

  const flushBatch = useCallback(async (brandId: number) => {
    batchTimerRef.current.delete(brandId);
    const waiters = batchRef.current.get(brandId) || [];
    batchRef.current.delete(brandId);
    if (waiters.length === 0) return;

    const contentIds = Array.from(new Set(waiters.map((w) => w.contentId)));
    try {
      const statuses = await getBulkPublishStatus(brandId, contentIds, tokenRef.current || undefined);
      const now = Date.now();
      for (const [cid, status] of Object.entries(statuses)) {
        cacheRef.current.set(`${brandId}:${cid}`, { status, timestamp: now });
      }
      waiters.forEach((w) => w.resolve(statuses[String(w.contentId)] || null));
    } catch (err) {
      console.debug('Bulk publish-status fetch failed:', err);
      waiters.forEach((w) => w.resolve(null));
    }
  }, []);

  const getPublishStatus = useCallback(
    (brandId: number, contentId: number): Promise<ContentPublishStatus | null> => {
      const cached = cacheRef.current.get(`${brandId}:${contentId}`);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return Promise.resolve(cached.status);
      }

      return new Promise((resolve) => {
        const waiters = batchRef.current.get(brandId) || [];
        waiters.push({ contentId, resolve });
        batchRef.current.set(brandId, waiters);

        if (!batchTimerRef.current.has(brandId)) {
          batchTimerRef.current.set(
            brandId,
            window.setTimeout(() => flushBatch(brandId), BATCH_WINDOW_MS)
          );
        }
      });
    },
    [flushBatch]
  );

  const refreshPublishStatus = useCallback(
    async (brandId: number, contentId: number): Promise<ContentPublishStatus | null> => {
      try {
        const status = await getContentPublishStatus(brandId, contentId, tokenRef.current || undefined);
        cacheRef.current.set(`${brandId}:${contentId}`, { status, timestamp: Date.now() });
        return status;
      } catch (err) {
        console.debug('Publish-status refresh failed:', err);
        return null;
      }
    },
    []
  );

  return (
    <PublishStatusContext.Provider value={{ getPublishStatus, refreshPublishStatus }}>
      {children}
    </PublishStatusContext.Provider>
  );
};

export const usePublishStatus = () => {
  const context = useContext(PublishStatusContext);
  if (!context) {
    throw new Error('usePublishStatus must be used within PublishStatusProvider');
  }
  return context;
};

export default PublishStatusContext;
